import type { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import {
  ChatHistoryEntry,
  ChatMessageType,
  Character,
  GameEventPayload,
  GamePhase,
  GameState,
  DebugSnapshot,
  PendingAction,
  PendingActionMetadata,
  Player,
  PlayerSnapshot,
  Role,
  ServerEvent,
} from "./types";
import { setupScene } from "./scene";
import { createCharacter } from "./character";
import { handleChat, ChatController, ChatMessageOptions } from "./chat";
import { PLAYER_COLOR_NAMES, DEAD_PLAYER_COLOR, resolveColorDetails } from "./colorPalette";
import { initDebugMode, DebugController } from "./debug";
import { Endscreen, GameOverData } from "./endscreen";
import { loadSettings, saveSettings, getAvailableModels, type GameSettings } from "./settings";
import { initLeaderboardUI, type LeaderboardController } from "./leaderboard";

// Mobile detection
function isMobileDevice(): boolean {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = width <= 768 || (width <= 1024 && height <= 768);
  
  // Check user agent for mobile browsers or DevTools emulation
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobileUA = /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  
  // Consider it mobile if:
  // 1. Small screen AND touch device, OR
  // 2. Mobile user agent detected
  const isMobile = (isTouchDevice && isSmallScreen) || isMobileUA;
  
  console.log("[Mobile Detection] Details:", {
    width,
    height,
    isTouchDevice,
    isSmallScreen,
    isMobileUA,
    userAgent: userAgent.substring(0, 50),
    result: isMobile
  });
  
  return isMobile;
}

const IS_MOBILE = isMobileDevice();
console.log("[Mobile Detection] IS_MOBILE:", IS_MOBILE, "viewport:", window.innerWidth, "x", window.innerHeight);

// Get API base URL from environment variable (set at build time by Vite)
// Fallback to localhost:8000 if not set
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";

if (new URLSearchParams(window.location.search).has("debug")) {
  localStorage.setItem("debug_mode", "true");
}
const debugModePreferred = localStorage.getItem("debug_mode") === "true";

interface ReconnectResponse {
  game_id?: string;
  game_active?: boolean;
  pending_action?: unknown;
  chat_history?: ChatHistoryEntry[];
  player?: PlayerSnapshot;
  phase?: string;
  round_number?: number;
}

type GameLifecycleStatus = "active" | "completed";

interface GameSummary {
  gameId: string;
  round: number;
  phase: GamePhase | null;
  winner: string | null;
  status: GameLifecycleStatus;
  updatedAt: number;
}

const STORAGE_KEYS = {
  ACTIVE_GAME_ID: "llm_mafia_active_game_id",
  GAME_IDS: "llm_mafia_game_ids",
  USER_ID: "llm_mafia_user_id",
  USERNAME: "llm_mafia_username",
} as const;

interface UserSession {
  user_id: string;  // Firestore document ID
  username: string;
}

let currentUser: UserSession | null = null;
let leaderboardController: LeaderboardController | null = null;

const gameState: GameState = {
  phase: GamePhase.SETUP,
  day: 1,
  players: [],
  messages: [],
  playerRole: null,
  humanPlayerName: null,
  humanPlayerColor: null,
  winner: null,
  pendingAction: null,
  gameId: null,
  connected: false,
  lastActionId: null,
  isPaused: false,
  debugEnabled: debugModePreferred,
};

const gameSummaries: Map<string, GameSummary> = new Map();
let gameIdOrder: string[] = [];
let activeGameId: string | null = null;

const debugController: DebugController = initDebugMode({
  onTogglePause: (shouldPause: boolean) => {
    void handleDebugToggle(shouldPause);
  },
});
gameState.debugEnabled = debugController.enabled;

let scene: any = null;
let camera: any = null;
let renderer: any = null;
let controls: OrbitControls | null = null;
let sceneCharacters: Character[] = [];

if (!IS_MOBILE) {
  const sceneSetup = setupScene();
  scene = sceneSetup.scene;
  camera = sceneSetup.camera;
  renderer = sceneSetup.renderer;
  controls = sceneSetup.controls;
  console.log("[3D Scene] Initialized for desktop");
} else {
  console.log("[3D Scene] Skipped for mobile");
  // Hide canvas on mobile
  const canvas = document.querySelector('canvas');
  if (canvas) canvas.style.display = 'none';
}

const playersByName = new Map<string, Player>();
const DEFAULT_PLAYER_COLOR = "#3498DB";
const chatController: ChatController = handleChat(gameState, submitPendingAction);
const endscreen = new Endscreen();
const statusRoundElement = document.getElementById("status-round") as HTMLDivElement | null;
const statusPhaseElement = document.getElementById("status-phase") as HTMLDivElement | null;
const statusRoleElement = document.getElementById("status-role") as HTMLDivElement | null;
const connectionStatusElement = document.getElementById("connection-status") as HTMLDivElement | null;
const userStatusNameElement = document.getElementById("user-status-name") as HTMLDivElement | null;
const connectionStatusLabel = document.getElementById(
  "status-connection-label"
) as HTMLSpanElement | null;
const rosterListElement = document.getElementById("player-roster") as HTMLUListElement | null;
const homeScreenElement = document.getElementById("home-screen") as HTMLDivElement | null;
const startGameButton = document.getElementById("start-game-button") as HTMLButtonElement | null;
const createRoomButton = document.getElementById("create-room-button") as HTMLButtonElement | null;
const joinRoomButton = document.getElementById("join-room-button") as HTMLButtonElement | null;
const playSoloButton = document.getElementById("play-solo-button") as HTMLButtonElement | null;
const leaderboardButton = document.getElementById("leaderboard-button") as HTMLButtonElement | null;
const quitGameButton = document.getElementById("quit-game-button") as HTMLButtonElement | null;
const settingsButton = document.getElementById("settings-button") as HTMLButtonElement | null;

// Update button text for mobile
if (IS_MOBILE && quitGameButton) {
  quitGameButton.textContent = "Quit";
}

const settingsModal = document.getElementById("settings-modal") as HTMLDivElement | null;
const settingsModalOverlay = document.getElementById("settings-modal") as HTMLDivElement | null;
const createRoomModal = document.getElementById("create-room-modal") as HTMLDivElement | null;
const joinRoomModal = document.getElementById("join-room-modal") as HTMLDivElement | null;
const modelSelect = document.getElementById("model-select") as HTMLSelectElement | null;
const reasoningSelect = document.getElementById("reasoning-select") as HTMLSelectElement | null;
const simulateToggle = document.getElementById("simulate-toggle") as HTMLInputElement | null;
const flipModeSelect = document.getElementById("flip-mode-select") as HTMLSelectElement | null;
const settingsSaveButton = document.getElementById("settings-save") as HTMLButtonElement | null;
const settingsCancelButton = document.getElementById("settings-cancel") as HTMLButtonElement | null;
const createRoomNameInput = document.getElementById("create-room-name-input") as HTMLInputElement | null;
const createPlayerNameInput = document.getElementById("create-player-name-input") as HTMLInputElement | null;
const createPlayerCapacitySelect = document.getElementById("create-player-capacity-select") as HTMLSelectElement | null;
const loginModal = document.getElementById("login-modal") as HTMLDivElement | null;
const loginUsernameInput = document.getElementById("login-username") as HTMLInputElement | null;
const loginSubmitButton = document.getElementById("login-submit") as HTMLButtonElement | null;
const loginErrorMessage = document.getElementById("login-error") as HTMLDivElement | null;
const createRoomPrivateToggle = document.getElementById("create-room-private-toggle") as HTMLInputElement | null;
const createRoomSubmitButton = document.getElementById("create-room-submit") as HTMLButtonElement | null;
const createRoomCancelButton = document.getElementById("create-room-cancel") as HTMLButtonElement | null;
const joinRoomCodeInput = document.getElementById("join-room-code-input") as HTMLInputElement | null;
const joinPlayerNameInput = document.getElementById("join-player-name-input") as HTMLInputElement | null;
const joinRoomSubmitButton = document.getElementById("join-room-submit") as HTMLButtonElement | null;
const joinRoomCancelButton = document.getElementById("join-room-cancel") as HTMLButtonElement | null;
const roleToggleButton = document.getElementById("role-toggle-button") as HTMLButtonElement | null;
const roleInfoPanel = document.getElementById("role-info-panel") as HTMLElement | null;
const mobilePlayerStatus = document.getElementById("mobile-player-status") as HTMLDivElement | null;

let currentGameSettings: GameSettings = loadSettings();

let websocket: WebSocket | null = null;
let reconnectTimer: number | undefined;
let pendingActionPollTimer: number | undefined;
let debugPollTimer: number | undefined;
let roleSyncTimer: number | undefined;
let debugToggleInFlight = false;
let autosaveTimer: number | undefined;
const AUTOSAVE_INTERVAL_MS = 30_000;
let reconnectAttempts = 0;
let lastReconnectMessageTime = 0;
let historyRestored = false;
let renderLoopStarted = false;

updateGameStatus();
updateRoster();

function animate(): void {
  if (!IS_MOBILE && renderer && scene && camera && controls) {
    requestAnimationFrame(animate);
    sceneCharacters.forEach((character) => {
      character.update();
    });
    controls.update();
    renderer.render(scene, camera);
  }
}

function ensureRenderLoop(): void {
  if (renderLoopStarted || IS_MOBILE) {
    return;
  }

  renderLoopStarted = true;
  animate();
}

function startGame(): void {
  ensureRenderLoop();
  loadUserSession();
  initializeLeaderboardController();
  checkAuthenticationStatus();
  initializeHomeScreen();
}

function initializeLeaderboardController(): void {
  if (leaderboardController) {
    return;
  }

  leaderboardController = initLeaderboardUI({
    apiBaseUrl: API_BASE_URL,
    authenticatedFetch,
    onOpen: () => {
      hideHomeScreen();
    },
    onClose: () => {
      showHomeScreen();
    },
    getCurrentUser: () => currentUser,
    onRequireLogin: () => {
      showLoginModal();
    },
  });
}

function loadUserSession(): void {
  const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
  const username = localStorage.getItem(STORAGE_KEYS.USERNAME);
  
  if (userId && username) {
    currentUser = {
      user_id: userId,  // Keep as string for Firestore
      username: username,
    };
    updateUserDisplay();
  }
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  if (currentUser) {
    headers["X-User-Id"] = currentUser.user_id;  // Already a string
    headers["X-Username"] = currentUser.username;
  }
  return headers;
}

async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const authHeaders = getAuthHeaders();
  const headers = {
    ...authHeaders,
    ...(options.headers as Record<string, string> || {}),
  };
  return fetch(url, { ...options, headers });
}

function checkAuthenticationStatus(): void {
  if (!currentUser) {
    showLoginModal();
  }
}

function showLoginModal(): void {
  if (loginModal) {
    loginModal.classList.remove("hidden");
    if (loginUsernameInput) {
      loginUsernameInput.focus();
      loginUsernameInput.value = "";
    }
    if (loginErrorMessage) {
      loginErrorMessage.style.display = "none";
    }
  }
  if (homeScreenElement) {
    homeScreenElement.classList.add("hidden");
  }
}

function hideLoginModal(): void {
  if (loginModal) {
    loginModal.classList.add("hidden");
  }
}

async function handleLogin(): Promise<void> {
  if (!loginUsernameInput || !loginErrorMessage) return;
  
  const username = loginUsernameInput.value.trim();
  
  if (!username) {
    loginErrorMessage.textContent = "Please enter a username";
    loginErrorMessage.style.display = "block";
    return;
  }
  
  if (username.length < 3 || username.length > 20) {
    loginErrorMessage.textContent = "Username must be 3-20 characters";
    loginErrorMessage.style.display = "block";
    return;
  }
  
  if (!/^[A-Za-z0-9_]+$/.test(username)) {
    loginErrorMessage.textContent = "Username can only contain letters, numbers, and underscores";
    loginErrorMessage.style.display = "block";
    return;
  }
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username }),
    });
    
    if (!response.ok) {
      const error = await response.text();
      loginErrorMessage.textContent = error || "Login failed";
      loginErrorMessage.style.display = "block";
      return;
    }
    
    const data = (await response.json()) as { user_id: string; username: string };  // Firestore returns string IDs
    
    currentUser = {
      user_id: data.user_id,
      username: data.username,
    };
    
    localStorage.setItem(STORAGE_KEYS.USER_ID, data.user_id);  // Already a string
    localStorage.setItem(STORAGE_KEYS.USERNAME, data.username);
    
    hideLoginModal();
    updateUserDisplay();
    showHomeScreen();
  } catch (error) {
    const message = error instanceof Error ? error.message : "Network error";
    loginErrorMessage.textContent = message;
    loginErrorMessage.style.display = "block";
  }
}

function updateUserDisplay(): void {
  if (currentUser && userStatusNameElement) {
    userStatusNameElement.textContent = currentUser.username;
  }
}

function showSettingsModal(): void {
  if (!settingsModalOverlay) return;
  
  // Load current settings into form
  currentGameSettings = loadSettings();
  
  if (modelSelect) {
    modelSelect.value = currentGameSettings.model;
  }
  if (reasoningSelect) {
    reasoningSelect.value = currentGameSettings.reasoningLevel;
  }
  if (simulateToggle) {
    simulateToggle.checked = currentGameSettings.simulateGame;
  }
  if (flipModeSelect) {
    flipModeSelect.value = currentGameSettings.flipMode;
  }
  
  settingsModalOverlay.classList.remove("hidden");
}

function hideSettingsModal(): void {
  if (!settingsModalOverlay) return;
  settingsModalOverlay.classList.add("hidden");
}

function handleSettingsSave(): void {
  if (!modelSelect || !reasoningSelect || !simulateToggle) return;
  
  currentGameSettings = {
    model: modelSelect.value,
    reasoningLevel: reasoningSelect.value as "disabled" | "low" | "medium" | "high",
    simulateGame: simulateToggle.checked,
    flipMode: (flipModeSelect?.value as GameSettings["flipMode"]) ?? "full",
  };
  
  saveSettings(currentGameSettings);
  hideSettingsModal();
  
  // Show confirmation
  addMessage("Settings saved successfully!", "system");
}

function handleSettingsCancel(): void {
  hideSettingsModal();
}

function initializeSettingsModal(): void {
  // Initialize login modal
  if (loginSubmitButton) {
    loginSubmitButton.onclick = () => {
      void handleLogin();
    };
  }

  if (loginUsernameInput) {
    loginUsernameInput.onkeypress = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        void handleLogin();
      }
    };
  }

  if (loginModal) {
    loginModal.onclick = (event: MouseEvent) => {
      if (event.target === loginModal) {
        // Don't allow clicking outside to close login modal
      }
    };
  }

  if (settingsButton) {
    settingsButton.onclick = () => {
      showSettingsModal();
    };
  }
  
  if (settingsSaveButton) {
    settingsSaveButton.onclick = () => {
      handleSettingsSave();
    };
  }
  
  if (settingsCancelButton) {
    settingsCancelButton.onclick = () => {
      handleSettingsCancel();
    };
  }
  
  // Close modal when clicking outside of it
  if (settingsModalOverlay) {
    settingsModalOverlay.onclick = (event: MouseEvent) => {
      if (event.target === settingsModalOverlay) {
        hideSettingsModal();
      }
    };
  }

  // Initialize create room modal
  if (createRoomSubmitButton) {
    createRoomSubmitButton.onclick = () => {
      void handleCreateRoomSubmit();
    };
  }
  
  if (createRoomCancelButton) {
    createRoomCancelButton.onclick = () => {
      hideCreateRoomModal();
    };
  }
  
  if (createRoomModal) {
    createRoomModal.onclick = (event: MouseEvent) => {
      if (event.target === createRoomModal) {
        hideCreateRoomModal();
      }
    };
  }

  // Initialize join room modal
  if (joinRoomSubmitButton) {
    joinRoomSubmitButton.onclick = () => {
      void handleJoinRoomSubmit();
    };
  }
  
  if (joinRoomCancelButton) {
    joinRoomCancelButton.onclick = () => {
      hideJoinRoomModal();
    };
  }
  
  if (joinRoomModal) {
    joinRoomModal.onclick = (event: MouseEvent) => {
      if (event.target === joinRoomModal) {
        hideJoinRoomModal();
      }
    };
  }
}

function initializeHomeScreen(): void {
  // Attach event listeners to old start-game-button for backward compatibility
  if (startGameButton) {
    startGameButton.onclick = () => {
      void handlePlaySolo();
    };
  }

  // Attach event listeners to new home screen buttons
  if (createRoomButton) {
    createRoomButton.onclick = () => {
      void showCreateRoomModal();
    };
  }

  if (joinRoomButton) {
    joinRoomButton.onclick = () => {
      void showJoinRoomModal();
    };
  }

  if (leaderboardButton) {
    leaderboardButton.onclick = () => {
      if (!leaderboardController) {
        initializeLeaderboardController();
      }
      if (!currentUser) {
        showLoginModal();
        return;
      }
      leaderboardController?.open();
    };
  }

  if (playSoloButton) {
    playSoloButton.onclick = () => {
      void handlePlaySolo();
    };
  }
  
  if (quitGameButton) {
    quitGameButton.onclick = () => {
      void handleQuitGame();
    };
  }

  // Login button event listener
  if (loginSubmitButton) {
    loginSubmitButton.onclick = () => {
      void handleLogin();
    };
  }

  // Login on Enter key
  if (loginUsernameInput) {
    loginUsernameInput.onkeydown = (event) => {
      if (event.key === "Enter") {
        void handleLogin();
      }
    };
  }

  // Role info toggle for mobile
  if (IS_MOBILE && roleToggleButton && roleInfoPanel) {
    roleToggleButton.style.display = "block";
    
    roleToggleButton.onclick = () => {
      const isExpanded = roleInfoPanel.classList.contains("expanded");
      if (isExpanded) {
        roleInfoPanel.classList.remove("expanded");
        roleToggleButton.textContent = "📋 Role";
      } else {
        roleInfoPanel.classList.add("expanded");
        roleToggleButton.textContent = "✕ Close";
      }
    };
    
    // Close role panel when clicking outside
    document.addEventListener("click", (event) => {
      if (roleInfoPanel.classList.contains("expanded") &&
          !roleInfoPanel.contains(event.target as Node) &&
          event.target !== roleToggleButton) {
        roleInfoPanel.classList.remove("expanded");
        roleToggleButton.textContent = "📋 Role";
      }
    });
  }

  // Role info toggle for desktop
  const desktopRoleToggleButton = document.getElementById("desktop-role-toggle-button") as HTMLButtonElement | null;
  const rolePanelCloseButton = document.getElementById("role-panel-close") as HTMLButtonElement | null;
  
  if (!IS_MOBILE && desktopRoleToggleButton && roleInfoPanel && rolePanelCloseButton) {
    // Panel starts open on desktop, toggle button hidden
    desktopRoleToggleButton.classList.remove("visible");
    
    // Close button functionality
    rolePanelCloseButton.onclick = () => {
      roleInfoPanel.classList.add("collapsed");
      desktopRoleToggleButton.classList.add("visible");
    };
    
    // Toggle button to reopen
    desktopRoleToggleButton.onclick = () => {
      roleInfoPanel.classList.remove("collapsed");
      desktopRoleToggleButton.classList.remove("visible");
    };
  }

  initializeSettingsModal();
  loadGameRegistry();
  updateGameSwitcher();

  if (activeGameId) {
    hideHomeScreen();
    void resumeActiveGame();
  } else {
    showHomeScreen();
  }
}

function loadGameRegistry(): void {
  const rawIds = localStorage.getItem(STORAGE_KEYS.GAME_IDS);
  let parsedIds: unknown;
  if (rawIds) {
    try {
      parsedIds = JSON.parse(rawIds);
    } catch (error) {
      console.warn("Failed to parse stored game identifiers", error);
    }
  }

  const ids =
    Array.isArray(parsedIds) && parsedIds.length > 0
      ? parsedIds.filter((value): value is string => typeof value === "string" && value.trim().length > 0)
      : [];

  gameIdOrder = Array.from(new Set(ids));
  for (const gameId of gameIdOrder) {
    if (!gameSummaries.has(gameId)) {
      gameSummaries.set(gameId, {
        gameId,
        round: 1,
        phase: GamePhase.SETUP,
        winner: null,
        status: "active",
        updatedAt: Date.now(),
      });
    }
  }

  const storedActive = localStorage.getItem(STORAGE_KEYS.ACTIVE_GAME_ID);
  if (storedActive && gameIdOrder.includes(storedActive)) {
    activeGameId = storedActive;
  } else {
    activeGameId = gameIdOrder.length > 0 ? gameIdOrder[0] : null;
    persistActiveGameSelection();
  }
}

function persistGameRegistry(): void {
  gameIdOrder = Array.from(new Set(gameIdOrder.filter((id) => id.trim().length > 0)));
  if (gameIdOrder.length === 0) {
    localStorage.removeItem(STORAGE_KEYS.GAME_IDS);
    return;
  }
  localStorage.setItem(STORAGE_KEYS.GAME_IDS, JSON.stringify(gameIdOrder));
}

function persistActiveGameSelection(): void {
  if (activeGameId) {
    localStorage.setItem(STORAGE_KEYS.ACTIVE_GAME_ID, activeGameId);
  } else {
    localStorage.removeItem(STORAGE_KEYS.ACTIVE_GAME_ID);
  }
}

function registerGame(gameId: string): void {
  const trimmedId = gameId.trim();
  if (!trimmedId) {
    return;
  }

  if (!gameIdOrder.includes(trimmedId)) {
    gameIdOrder.push(trimmedId);
  }

  const existingSummary = gameSummaries.get(trimmedId);
  const now = Date.now();
  gameSummaries.set(trimmedId, {
    gameId: trimmedId,
    round: existingSummary?.round ?? 1,
    phase: existingSummary?.phase ?? GamePhase.SETUP,
    winner: existingSummary?.winner ?? null,
    status: existingSummary?.status === "completed" ? "completed" : "active",
    updatedAt: now,
  });

  activeGameId = trimmedId;
  persistGameRegistry();
  persistActiveGameSelection();
}

function markGameCompleted(gameId: string, winner: string | null): void {
  const summary = gameSummaries.get(gameId);
  if (!summary) {
    return;
  }

  gameSummaries.set(gameId, {
    ...summary,
    winner: winner ?? summary.winner,
    status: "completed",
    updatedAt: Date.now(),
  });
  updateGameSwitcher();
}

function removeGameFromRegistry(gameId: string): void {
  gameSummaries.delete(gameId);
  gameIdOrder = gameIdOrder.filter((id) => id !== gameId);
  if (activeGameId === gameId) {
    activeGameId = gameIdOrder[0] ?? null;
  }
  persistGameRegistry();
  persistActiveGameSelection();
  updateGameSwitcher();
}

function showHomeScreen(): void {
  if (homeScreenElement) {
    homeScreenElement.classList.remove("hidden");
  }
  hideGameSwitcher();
}

function hideHomeScreen(): void {
  if (homeScreenElement) {
    homeScreenElement.classList.add("hidden");
  }
}

function showGameSwitcher(): void {
  // Game switcher panel removed - keeping empty function for compatibility
}

function hideGameSwitcher(): void {
  // Game switcher panel removed - keeping empty function for compatibility
}

function updateGameSwitcher(): void {
  // Game switcher panel removed - keeping empty function for compatibility
}

function setHomeScreenButtonsDisabled(disabled: boolean): void {
  if (startGameButton) {
    startGameButton.disabled = disabled;
  }
  if (createRoomButton) {
    createRoomButton.disabled = disabled;
  }
  if (joinRoomButton) {
    joinRoomButton.disabled = disabled;
  }
  if (leaderboardButton) {
    leaderboardButton.disabled = disabled;
  }
  if (playSoloButton) {
    playSoloButton.disabled = disabled;
  }
  if (quitGameButton) {
    quitGameButton.disabled = disabled;
  }
}

async function handleStartGameRequest(): Promise<void> {
  setHomeScreenButtonsDisabled(true);
  const previousActive = activeGameId;
  try {
    await createAndActivateNewGame();
  } catch (error) {
    const message = error instanceof Error ? error.message : "Failed to start a new game.";
    addMessage(`Error: ${message}`, "system");
    if (previousActive) {
      try {
        await switchToGame(previousActive, { forceReconnect: true });
      } catch (resumeError) {
        console.error("Failed to restore previous game after error", resumeError);
        showHomeScreen();
      }
    } else {
      showHomeScreen();
    }
  } finally {
    setHomeScreenButtonsDisabled(false);
  }
}

async function handlePlaySolo(): Promise<void> {
  // Check if user is logged in
  if (!currentUser) {
    showLoginModal();
    return;
  }
  
  setHomeScreenButtonsDisabled(true);
  const previousActive = activeGameId;
  try {
    await createAndActivateNewGame();
  } catch (error) {
    const message = error instanceof Error ? error.message : "Failed to start a new game.";
    addMessage(`Error: ${message}`, "system");
    if (previousActive) {
      try {
        await switchToGame(previousActive, { forceReconnect: true });
      } catch (resumeError) {
        console.error("Failed to restore previous game after error", resumeError);
        showHomeScreen();
      }
    } else {
      showHomeScreen();
    }
  } finally {
    setHomeScreenButtonsDisabled(false);
  }
}

async function showCreateRoomModal(): Promise<void> {
  if (!createRoomModal) return;
  
  // Clear and show modal
  if (createRoomNameInput) createRoomNameInput.value = "";
  if (createPlayerNameInput) createPlayerNameInput.value = "";
  if (createPlayerCapacitySelect) createPlayerCapacitySelect.value = "9";
  if (createRoomPrivateToggle) createRoomPrivateToggle.checked = false;
  
  createRoomModal.classList.remove("hidden");
}

function hideCreateRoomModal(): void {
  if (createRoomModal) {
    createRoomModal.classList.add("hidden");
  }
}

async function showJoinRoomModal(): Promise<void> {
  if (!joinRoomModal) return;
  
  // Clear and show modal
  if (joinRoomCodeInput) joinRoomCodeInput.value = "";
  if (joinPlayerNameInput) joinPlayerNameInput.value = "";
  
  joinRoomModal.classList.remove("hidden");
}

function hideJoinRoomModal(): void {
  if (joinRoomModal) {
    joinRoomModal.classList.add("hidden");
  }
}

async function handleCreateRoomSubmit(): Promise<void> {
  const roomName = createRoomNameInput?.value?.trim();
  const playerName = createPlayerNameInput?.value?.trim();
  const playerCapacity = createPlayerCapacitySelect?.value;
  const isPrivate = createRoomPrivateToggle?.checked ?? false;

  if (!roomName) {
    addMessage("Please enter a room name", "system");
    return;
  }
  if (!playerName) {
    addMessage("Please enter your name", "system");
    return;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/rooms`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        room_name: roomName,
        player_capacity: parseInt(playerCapacity || "9", 10),
        private: isPrivate,
      }),
    });
    if (!response.ok) throw new Error("Failed to create room");
    const roomData = (await response.json()) as { room_id?: string };
    if (roomData.room_id) {
      hideCreateRoomModal();
      addMessage(`Room "${roomName}" created! Joining as ${playerName}...`, "system");
      // TODO: Navigate to room lobby instead of starting game
      console.log("Created room:", roomData.room_id, "Player:", playerName);
      // For now, just start the game solo
      void handlePlaySolo();
    }
  } catch (error) {
    console.error("Error creating room:", error);
    addMessage("Failed to create room. Please try again.", "system");
  }
}

async function handleJoinRoomSubmit(): Promise<void> {
  const roomCode = joinRoomCodeInput?.value?.trim();
  const playerName = joinPlayerNameInput?.value?.trim();

  if (!roomCode) {
    addMessage("Please enter a room code", "system");
    return;
  }
  if (!playerName) {
    addMessage("Please enter your name", "system");
    return;
  }

  try {
    // First, join the room
    const response = await fetch(`${API_BASE_URL}/api/rooms/${roomCode}/join`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        username: playerName,
      }),
    });
    if (!response.ok) throw new Error("Failed to join room");
    const roomData = (await response.json()) as { room_id?: string };
    if (roomData.room_id) {
      hideJoinRoomModal();
      addMessage(`Joined room! Waiting for the game to start...`, "system");
      // TODO: Navigate to room lobby instead of starting game
      console.log("Joined room:", roomData.room_id, "Player:", playerName);
      // For now, just start the game solo
      void handlePlaySolo();
    }
  } catch (error) {
    console.error("Error joining room:", error);
    addMessage("Failed to join room. Check the code and try again.", "system");
  }
}

async function createAndActivateNewGame(): Promise<void> {
  disconnectActiveSession();
  hideHomeScreen();
  await initializeGame();
}

async function switchToGame(
  gameId: string,
  options: { forceReconnect?: boolean } = {}
): Promise<void> {
  if (!gameId) {
    return;
  }

  if (!options.forceReconnect && gameState.gameId === gameId && gameState.connected) {
    return;
  }

  registerGame(gameId);
  updateGameSwitcher();
  hideHomeScreen();
  disconnectActiveSession();

  try {
    await reconnectToGame(gameId);
  } catch (error) {
    removeGameFromRegistry(gameId);
    showHomeScreen();
    throw error;
  }
}

async function resumeActiveGame(): Promise<void> {
  if (!activeGameId) {
    return;
  }
  
  const savedGameId = activeGameId; // Capture for type safety
  
  // Validate that the game still exists on the backend before trying to reconnect
  try {
    const validateResponse = await fetch(`${API_BASE_URL}/api/game/${savedGameId}/debug`);
    if (!validateResponse.ok) {
      // Game doesn't exist anymore - clean up localStorage
      console.warn(`Game ${savedGameId} no longer exists on backend, clearing from localStorage`);
      activeGameId = null;
      removeGameFromRegistry(savedGameId);
      persistActiveGameSelection();
      showHomeScreen();
      return;
    }
  } catch (error) {
    console.error("Failed to validate game existence:", error);
    // On network error, still try to reconnect (might be temporary)
  }
  
  try {
    await switchToGame(savedGameId, { forceReconnect: true });
  } catch (error) {
    console.error("Failed to resume active game", error);
    // Clean up if reconnection failed
    activeGameId = null;
    removeGameFromRegistry(savedGameId);
    persistActiveGameSelection();
    showHomeScreen();
  }
}

function disconnectActiveSession(): void {
  if (pendingActionPollTimer) {
    clearInterval(pendingActionPollTimer);
    pendingActionPollTimer = undefined;
  }
  if (roleSyncTimer) {
    clearInterval(roleSyncTimer);
    roleSyncTimer = undefined;
  }
  if (reconnectTimer) {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = undefined;
  }
  stopAutosave();
  stopDebugPolling();
  if (websocket) {
    websocket.close();
    websocket = null;
  }
  resetPlayerRegistry();
  resetChat();
  gameState.connected = false;
  gameState.gameId = null;
  gameState.humanPlayerName = null;
  gameState.humanPlayerColor = null;
  gameState.winner = null;
  gameState.pendingAction = null;
  gameState.lastActionId = null;
  gameState.phase = GamePhase.SETUP;
  gameState.day = 1;
  gameState.playerRole = null;
  handleGameIdUpdate();
  updateGameStatus();
}

async function handleQuitGame(): Promise<void> {
  const currentGameId = gameState.gameId;
  
  // Clean up frontend state first
  disconnectActiveSession();
  resetPlayerRegistry();
  resetChat();
  
  // Reset game state
  gameState.gameId = null;
  gameState.day = 1;
  gameState.phase = GamePhase.SETUP;
  gameState.winner = null;
  gameState.playerRole = null;
  gameState.humanPlayerName = null;
  gameState.humanPlayerColor = null;
  gameState.pendingAction = null;
  gameState.connected = false;
  gameState.lastActionId = null;
  gameState.isPaused = false;
  
  // Clear storage and UI
  removeGameFromRegistry(currentGameId || "");
  activeGameId = null;
  persistActiveGameSelection();
  persistGameRegistry();
  showHomeScreen();
  hideGameSwitcher();
  initializeHomeScreen();
  
  // Notify backend to quit the game (do this last)
  if (currentGameId) {
    try {
      await fetch(`${API_BASE_URL}/api/game/${currentGameId}/quit`, {
        method: "POST",
      });
      console.log(`Game ${currentGameId} quit successfully`);
    } catch (error) {
      console.error("Error quitting game on backend:", error);
    }
  }
}

async function initializeGame(): Promise<void> {
  try {
    historyRestored = false;
    resetPlayerRegistry();
    resetChat();
    
    // Load current settings
    currentGameSettings = loadSettings();
    
    const response = await authenticatedFetch(`${API_BASE_URL}/api/game/start`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: currentGameSettings.model,
        reasoning_level: currentGameSettings.reasoningLevel,
        simulate: currentGameSettings.simulateGame,
      }),
    });
    if (!response.ok) {
      const errorText = await safeReadText(response);
      throw new Error(errorText || "Failed to start a new game.");
    }
    const data = (await response.json()) as { game_id?: string };
    if (!data.game_id) {
      throw new Error("Game server did not return a game identifier.");
    }
    gameState.gameId = data.game_id;
    registerGame(data.game_id);
    handleGameIdUpdate();
    updateGameSwitcher();
    hideHomeScreen();
    showGameSwitcher();
    updateGameStatus();
    addMessage("Game session created. Waiting for the first prompt...", "system");
    connectToEventStream();
    await refreshPendingAction();
    
    // Start polling for pending actions every 500ms
    if (pendingActionPollTimer) {
      clearInterval(pendingActionPollTimer);
    }
    pendingActionPollTimer = window.setInterval(refreshPendingAction, 500);

    // Start periodic role sync every 2 seconds - ensures role displays immediately
    if (roleSyncTimer) {
      clearInterval(roleSyncTimer);
    }
    roleSyncTimer = window.setInterval(() => {
      void syncPlayerRole();
    }, 2000);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : "Unexpected error while starting game.";
    addMessage(`Error: ${message}`, "system");
    if (!activeGameId) {
      showHomeScreen();
    }
    throw error instanceof Error ? error : new Error(message);
  }
}

async function reconnectToGame(gameId: string): Promise<void> {
  try {
    gameState.gameId = gameId;
    handleGameIdUpdate();
    registerGame(gameId);
    updateGameSwitcher();
    showGameSwitcher();
    updateGameStatus();

    addMessage("Welcome back! Reconnecting to your game...", "system");
    resetPlayerRegistry();
    resetChat();

    const response = await fetch(`${API_BASE_URL}/api/game/reconnect/${gameId}`);
    if (!response.ok) {
      const errorText = await safeReadText(response);
      throw new Error(errorText || "Failed to reconnect to an existing game.");
    }

    const data = (await response.json()) as ReconnectResponse;

    if (data.player) {
      updatePlayerFromSnapshot(data.player);
    }

    if (typeof data.phase === "string") {
      const normalizedPhase = normalizePhase(data.phase);
      if (normalizedPhase) {
        gameState.phase = normalizedPhase;
      }
    }

    if (typeof data.round_number === "number" && !Number.isNaN(data.round_number)) {
      gameState.day = Math.max(1, data.round_number);
    }

    updateGameStatus();

    if (Array.isArray(data.chat_history)) {
      await restoreChatHistory(data.chat_history);
    }

    if (data.pending_action) {
      handlePendingAction(data.pending_action as Record<string, unknown>);
    } else {
      chatController.setPrompt(null);
    }

    connectToEventStream();

    if (pendingActionPollTimer) {
      clearInterval(pendingActionPollTimer);
    }
    pendingActionPollTimer = window.setInterval(refreshPendingAction, 500);

    // Start periodic role sync on reconnect
    if (roleSyncTimer) {
      clearInterval(roleSyncTimer);
    }
    roleSyncTimer = window.setInterval(() => {
      void syncPlayerRole();
    }, 2000);

    addMessage("Successfully reconnected to your game session!", "system");
  } catch (error) {
    const message = error instanceof Error ? error.message : "Failed to reconnect";
    addMessage(`Error: ${message}`, "system");
    gameState.gameId = null;
    handleGameIdUpdate();
    updateGameStatus();
    removeGameFromRegistry(gameId);
    showHomeScreen();
    throw error;
  }
}

async function refreshPendingAction(): Promise<void> {
  try {
    const pendingUrl = new URL(`${API_BASE_URL}/api/game/pending`);
    if (gameState.gameId) {
      pendingUrl.searchParams.set("game_id", gameState.gameId);
    }

    const response = await authenticatedFetch(pendingUrl.toString());
    if (!response.ok) {
      return;
    }
    const data = (await response.json()) as { pending_action?: unknown };
    if (data.pending_action) {
      handlePendingAction(data.pending_action as Record<string, unknown>);
    }
  } catch (error) {
    console.error("Failed to refresh pending action", error);
  }
}

async function syncPlayerRole(): Promise<void> {
  try {
    if (!gameState.gameId) {
      console.log("[DEBUG] syncPlayerRole: no gameId");
      return;
    }
    console.log("[DEBUG] syncPlayerRole: syncing role from backend");
    const response = await authenticatedFetch(`${API_BASE_URL}/api/game/${gameState.gameId}/debug`);
    if (!response.ok) {
      console.log("[DEBUG] syncPlayerRole: response not ok");
      return;
    }
    const data = (await response.json()) as { player?: PlayerSnapshot };
    if (data.player?.role) {
      const role = parseRole(data.player.role);
      if (role && role !== gameState.playerRole) {
        console.log("[DEBUG] syncPlayerRole: updating role from", gameState.playerRole, "to", role);
        gameState.playerRole = role;
        updateRoleInfoPanel(role);
        updateGameStatus();
      }
    }
  } catch (error) {
    console.error("[DEBUG] syncPlayerRole error:", error);
  }
}

async function fetchGameState(): Promise<void> {
  try {
    if (!gameState.gameId) {
      console.log("[DEBUG] fetchGameState: no gameId");
      return;
    }
    console.log("[DEBUG] fetchGameState: fetching state for gameId=", gameState.gameId);
    const response = await fetch(`${API_BASE_URL}/api/game/${gameState.gameId}/debug`);
    if (!response.ok) {
      console.log("[DEBUG] fetchGameState: response not ok, status=", response.status);
      return;
    }
    const data = (await response.json()) as { player?: PlayerSnapshot };
    console.log("[DEBUG] fetchGameState: got data=", data);
    if (data.player) {
      console.log("[DEBUG] fetchGameState: updating from snapshot, role=", data.player.role);
      updatePlayerFromSnapshot(data.player);
    } else {
      console.log("[DEBUG] fetchGameState: no player in response");
    }
  } catch (error) {
    console.error("Failed to fetch game state", error);
  }
}

function connectToEventStream(): void {
  if (!gameState.gameId) {
    return;
  }

  if (websocket) {
    websocket.close();
    websocket = null;
  }
  if (reconnectTimer) {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = undefined;
  }

  const wsUrl = buildWsUrl(gameState.gameId);
  websocket = new WebSocket(wsUrl);

  websocket.onopen = () => {
    gameState.connected = true;
    reconnectAttempts = 0; // Reset on successful connection
    updateGameStatus();
    void fetchGameState(); // Fetch current player state after connecting
    void syncPlayerRole(); // Also sync role immediately on connect
    try {
      websocket?.send(JSON.stringify({ type: "ping" }));
    } catch (error) {
      console.warn("WebSocket ping failed", error);
    }
  };

  websocket.onmessage = (event) => {
    handleServerEvent(event.data);
  };

  websocket.onerror = (event) => {
    console.error("WebSocket error", event);
  };

  websocket.onclose = () => {
    gameState.connected = false;
    updateGameStatus();
    // Don't disable input - polling will continue to work
    // Only reconnect if we have an active pending action
    if (!gameState.winner && gameState.pendingAction) {
      // Silently attempt reconnect without spamming messages
      scheduleReconnect();
    }
  };
}

function scheduleReconnect(): void {
  if (reconnectTimer || !gameState.gameId || gameState.winner) {
    return;
  }

  reconnectAttempts++;
  
  // Show message only every 5+ seconds to avoid spam
  const now = Date.now();
  if (now - lastReconnectMessageTime > 5000) {
    addMessage("Connection lost. Reconnecting...", "system");
    lastReconnectMessageTime = now;
  }

  // Exponential backoff: 2s, 4s, 8s, max 30s
  const delay = Math.min(2000 * Math.pow(1.5, reconnectAttempts - 1), 30000);

  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = undefined;
    connectToEventStream();
  }, delay);
}

function handleServerEvent(rawData: string): void {
  let eventData: ServerEvent<Record<string, unknown>>;
  try {
    eventData = JSON.parse(rawData) as ServerEvent<Record<string, unknown>>;
  } catch (error) {
    console.error("Failed to parse WebSocket event", error, rawData);
    return;
  }

  const { type, payload } = eventData;

  // Filter events by game_id to prevent cross-game message bleeding
  const eventGameId = (eventData as any).game_id as string | undefined;
  if (eventGameId && gameState.gameId && eventGameId !== gameState.gameId) {
    console.debug(`[WEBSOCKET] Ignoring event from different game: ${eventGameId} (current: ${gameState.gameId})`);
    return;
  }

  // Log ALL events to see what's coming through
  console.log(`[WEBSOCKET EVENT] ${type}`, payload);

  // Debug logging for important events
  if (type === "player_response" || type === "player_prompt" || type === "pending_action") {
    console.log(`[DEBUG] Event: ${type}`, {
      payload,
      currentPhase: gameState.phase,
      currentRole: gameState.playerRole,
    });
  }

  switch (type) {
    case "pending_action":
    case "await_player_action":
      handlePendingAction(payload);
      break;
    case "player_prompt":
      handlePlayerPrompt(payload);
      break;
    case "player_setup":
      console.log("[DEBUG] player_setup event received:", payload);
      handlePlayerSetup(payload);
      break;
    case "human_player_assigned":
      console.log("[DEBUG] human_player_assigned event received:", payload);
      handleHumanPlayerAssigned(payload);
      break;
    case "phase_changed":
      handlePhaseChanged(payload);
      break;
    case "player_response":
      console.log("[DEBUG] About to handle player_response:", payload);
      handlePlayerResponse(payload);
      break;
    case "player_action":
      handlePlayerAction(payload);
      break;
    case "player_action_timeout":
      handleActionTimeout(payload);
      break;
    case "player_action_fallback":
      handleActionFallback(payload);
      break;
    case "game_event":
      handleGameEvent(payload);
      break;
    case "player_role_briefing":
      handleRoleBriefing(payload, eventData.timestamp);
      break;
    case "player_died":
      handlePlayerDied(payload);
      break;
    case "game_warning":
      handleGameWarning(payload);
      break;
    case "game_error":
      handleGameError(payload);
      break;
    case "game_started":
      addMessage("The game has started!", "system");
      // Spawn all players immediately when game starts
      void spawnAllPlayers();
      break;
    case "game_ended":
      handleGameEnded(payload);
      break;
    default:
      console.debug("Unhandled event type:", type, payload);
  }
}

function handlePendingAction(payload: Record<string, unknown> | undefined): void {
  if (!payload) {
    return;
  }

  const actionId = typeof payload.action_id === "string" ? payload.action_id : null;
  if (!actionId) {
    return;
  }

  let prompt = typeof payload.prompt === "string" ? payload.prompt : "";
  const metadata = (payload.metadata as PendingActionMetadata) ?? {};
  const playerSnapshot =
    (payload.player as PlayerSnapshot) ??
    (payload.player_snapshot as PlayerSnapshot) ??
    (payload.player_info as PlayerSnapshot) ??
    {};

  const isNewAction = gameState.lastActionId !== actionId;

  gameState.pendingAction = {
    actionId,
    prompt,
    metadata,
    player: playerSnapshot,
  };
  gameState.lastActionId = actionId;

  applyMetadata(metadata);
  updatePlayerFromSnapshot(playerSnapshot);

  // For human players, extract just the action description (not the full system prompt)
  let displayPrompt = prompt;
  let isGreetingPhase = false;
  
  if (gameState.humanPlayerName) {
    // Check if this is a greeting phase - skip showing the full prompt
    const phase = normalizePhase(
      typeof metadata.phase === "string"
        ? metadata.phase
        : typeof metadata.phase_type === "string"
          ? metadata.phase_type
          : undefined
    );
    
    if (phase === GamePhase.GREETING) {
      // For greeting phase, don't show the detailed prompt
      isGreetingPhase = true;
      displayPrompt = "Please introduce yourself with a brief greeting (1-2 sentences).";
    } else {
      // Find lines that contain actual instructions (skip THINKING tags, format examples, etc.)
      const lines = prompt.split("\n");
      const instructionLines: string[] = [];
      
      for (const line of lines) {
        const trimmed = line.trim();
        // Skip empty lines, template syntax, format examples, and AI-specific instructions
        if (trimmed.length === 0 || 
            trimmed.includes("<THINKING") ||
            trimmed.includes("Format:") ||
            trimmed.includes("Example") ||
            trimmed.includes("IMPORTANT INSTRUCTIONS") ||
            trimmed.includes("GAME RULES") ||
            trimmed.includes("During DAY") ||
            trimmed.includes("During NIGHT") ||
            trimmed.includes("All players:") ||
            trimmed.includes("PERSONALITY:") ||
            trimmed.includes("GREETING PHASE") ||
            trimmed.includes("DO NOT") ||
            trimmed.includes("Wait for the strategy") ||
            trimmed.includes("Current game state:")) {
          continue;
        }
        instructionLines.push(trimmed);
      }
      
      // Use the most relevant instruction lines (last 1-2 lines for human players)
      if (instructionLines.length > 0) {
        displayPrompt = instructionLines.slice(-2).join("\n");
      }
    }
  }

  // For human players, show a simplified prompt
  if (displayPrompt && displayPrompt.length > 200) {
    // If prompt is too long, extract just the action part
    const actionMatch = displayPrompt.match(/Respond with.*?[.!]/i) || 
                       displayPrompt.match(/Share your.*?[.!]/i) ||
                       displayPrompt.match(/Vote.*?[.!]/i) ||
                       displayPrompt.match(/Nominate.*?[.!]/i);
    if (actionMatch) {
      displayPrompt = actionMatch[0];
    } else {
      // Fallback: just take the last sentence
      const sentences = displayPrompt.split(/[.!]\s+/);
      displayPrompt = sentences[sentences.length - 1] || displayPrompt.slice(-150);
    }
  }

  chatController.setPrompt(displayPrompt);
  // Don't add greeting phase prompts to chat - they're too verbose and unnecessary
  if (isNewAction && !isGreetingPhase) {
    addMessage(displayPrompt, "system");
  }
  
  // Check if this is a voting phase - enable skip vote button if so
  const isVotingPhase = metadata.action === "day_voting";
  const canInteract = isLocalPlayerAlive() && !gameState.isPaused;
  chatController.updateSkipVoteButton(isVotingPhase, canInteract);
  chatController.updateActionButtons();

  chatController.setInputEnabled(canInteract);
  updateGameStatus();
}

function handlePlayerPrompt(payload: Record<string, unknown> | undefined): void {
  if (!payload) {
    return;
  }
  const prompt = typeof payload.prompt === "string" ? payload.prompt : null;
  const metadata = (payload.metadata as PendingActionMetadata) ?? {};
  applyMetadata(metadata);
  if (prompt) {
    chatController.setPrompt(prompt);
    // Only add message for LLM players, not for human player
    // (human player prompts are shown through pending_action event)
    const isHumanPlayer = typeof payload.is_human === "boolean" ? payload.is_human : false;
    if (!isHumanPlayer) {
      addMessage(prompt, "system");
    }
  }
}

function handlePlayerSetup(payload: Record<string, unknown> | undefined): void {
  if (!payload) {
    return;
  }
  const playerName =
    typeof payload.player_display_name === "string"
      ? payload.player_display_name
      : typeof payload.player_name === "string"
        ? payload.player_name
        : null;

  if (!playerName) {
    return;
  }

  const player = getOrCreatePlayer(playerName);
  const parsedRole = parseRole(payload.role);
  if (parsedRole) {
    player.actualRole = parsedRole;
    if (!player.role) {
      player.role = parsedRole;
    }
  }
  player.isAlive = true;
  updateRoster();

  // If this is the human player, update the gameState role
  if (playerName === gameState.humanPlayerName && parsedRole && gameState.playerRole !== parsedRole) {
    gameState.playerRole = parsedRole;
    updateRoleInfoPanel(parsedRole);
    updateGameStatus();
  }
}

function updateRoleInfoPanel(role: Role | null): void {
  console.log("[DEBUG] updateRoleInfoPanel called with role=", role);
  
  const roleInfoPanel = document.getElementById("role-info-panel");
  const roleDescription = document.getElementById("role-description");
  const teammateInfo = document.getElementById("teammate-info");
  
  if (!roleInfoPanel || !roleDescription || !teammateInfo) {
    console.log("[DEBUG] updateRoleInfoPanel: missing DOM elements", {
      roleInfoPanel: !!roleInfoPanel,
      roleDescription: !!roleDescription,
      teammateInfo: !!teammateInfo,
    });
    return;
  }
  
  if (!role) {
    console.log("[DEBUG] updateRoleInfoPanel: role is null, clearing panel");
    return;
  }

  // Role descriptions
  const roleDescriptions: Record<string, string> = {
    [Role.MAFIA]: "You are part of the mafia. At night, work with your teammates to eliminate a villager.",
    [Role.DOCTOR]: "You are the doctor. Each night, you can protect one player from elimination.",
    [Role.SHERIFF]: "You are the sheriff. Each day, you can check one player to learn if they are mafia.",
    [Role.VILLAGER]: "You are a villager. During the day, vote to eliminate suspected mafia members.",
  };

  // Update role name and description
  const existingHeader = roleDescription.querySelector(".panel-header");
  if (existingHeader) {
    existingHeader.remove();
  }
  
  const existingRoleName = roleDescription.querySelector(".role-name");
  if (existingRoleName) {
    existingRoleName.remove();
  }
  
  const existingRoleInfo = roleDescription.querySelector(".role-info");
  if (existingRoleInfo) {
    existingRoleInfo.remove();
  }

  const header = document.createElement("div");
  header.className = "panel-header";
  console.log("[DEBUG] updateRoleInfoPanel: humanPlayerColor=", gameState.humanPlayerColor, "role=", role);
  header.textContent = formatRoleLabel(role);
  roleDescription.appendChild(header);

  const roleName = document.createElement("div");
  roleName.className = `role-name ${role.toLowerCase()}`;
  roleName.textContent = role.toUpperCase();
  roleDescription.appendChild(roleName);

  const roleInfo = document.createElement("div");
  roleInfo.className = "role-info";
  roleInfo.textContent = roleDescriptions[role] || "Unknown role";
  roleDescription.appendChild(roleInfo);

  console.log("[DEBUG] updateRoleInfoPanel: role panel updated with role=", role);

  // Update teammate info if Mafia
  if (role === Role.MAFIA) {
    console.log("[DEBUG] updateRoleInfoPanel: checking for Mafia teammates");
    console.log("[DEBUG] humanPlayerName:", gameState.humanPlayerName);
    console.log("[DEBUG] All players:", Array.from(playersByName.keys()));
    const teammates = Array.from(playersByName.values()).filter(
      (p) => (p.role === Role.MAFIA || p.actualRole === Role.MAFIA) && p.name !== gameState.humanPlayerName
    );

    console.log("[DEBUG] updateRoleInfoPanel: found", teammates.length, "teammates, they are:", teammates.map(t => t.name));

    if (teammates.length > 0) {
      teammateInfo.classList.remove("hidden");
      
      // Clear existing teammates
      const existingHeader = teammateInfo.querySelector(".teammate-header");
      const existingList = teammateInfo.querySelector(".teammate-list");
      if (existingHeader) existingHeader.remove();
      if (existingList) existingList.remove();

      const header = document.createElement("div");
      header.className = "teammate-header";
      header.textContent = "Your Teammates";
      teammateInfo.appendChild(header);

      const list = document.createElement("ul");
      list.className = "teammate-list";
      teammates.forEach((teammate) => {
        const item = document.createElement("li");
        item.className = "teammate-item";
        item.textContent = teammate.name;
        list.appendChild(item);
      });
      teammateInfo.appendChild(list);
    } else {
      console.log("[DEBUG] updateRoleInfoPanel: no teammates, hiding teammate info");
      teammateInfo.classList.add("hidden");
    }
  } else {
    // Hide teammate info for non-Mafia roles
    teammateInfo.classList.add("hidden");
  }

  // Update commands section based on role
  const roleCommandsDiv = document.getElementById("role-commands");
  if (roleCommandsDiv) {
    updateRoleCommands(roleCommandsDiv, role);
  }
}

function updateRoleCommands(commandsDiv: HTMLElement, role: Role): void {
  // Define commands for each role
  const roleCommands: Record<Role, Array<{ name: string; when: string }>> = {
    [Role.VILLAGER]: [
      { name: "VOTE: PlayerName", when: "Vote to eliminate someone" },
      { name: "NOMINATE: PlayerName", when: "Nominate during day nominations" },
      { name: "ABSTAIN", when: "Skip voting" },
    ],
    [Role.MAFIA]: [
      { name: "VOTE: PlayerName", when: "Vote to eliminate someone" },
      { name: "NOMINATE: PlayerName", when: "Nominate during day nominations" },
      { name: "KILL: PlayerName", when: "Choose target at night (with team)" },
      { name: "ABSTAIN", when: "Skip voting" },
    ],
    [Role.DOCTOR]: [
      { name: "VOTE: PlayerName", when: "Vote to eliminate someone" },
      { name: "NOMINATE: PlayerName", when: "Nominate during day nominations" },
      { name: "PROTECT: PlayerName", when: "Protect a player at night" },
      { name: "ABSTAIN", when: "Skip voting" },
    ],
    [Role.SHERIFF]: [
      { name: "VOTE: PlayerName", when: "Vote to eliminate someone" },
      { name: "NOMINATE: PlayerName", when: "Nominate during day nominations" },
      { name: "CHECK: PlayerName", when: "Investigate a player at night" },
      { name: "ABSTAIN", when: "Skip voting" },
    ],
  };

  // Clear existing commands
  while (commandsDiv.firstChild) {
    commandsDiv.removeChild(commandsDiv.firstChild);
  }

  const commands = roleCommands[role] || [];
  if (commands.length === 0) {
    commandsDiv.classList.add("hidden");
    return;
  }

  commandsDiv.classList.remove("hidden");

  const header = document.createElement("div");
  header.className = "commands-header";
  header.textContent = "Chat Commands";
  commandsDiv.appendChild(header);

  const list = document.createElement("ul");
  list.className = "commands-list";
  commands.forEach((command) => {
    const item = document.createElement("li");
    item.className = "command-item";

    const nameEl = document.createElement("div");
    nameEl.className = "command-name";
    nameEl.textContent = command.name;
    item.appendChild(nameEl);

    const whenEl = document.createElement("div");
    whenEl.className = "command-when";
    whenEl.textContent = command.when;
    item.appendChild(whenEl);

    list.appendChild(item);
  });
  commandsDiv.appendChild(list);
}

function handleHumanPlayerAssigned(payload: Record<string, unknown> | undefined): void {
  if (!payload) {
    console.log("[DEBUG] handleHumanPlayerAssigned: payload is undefined");
    return;
  }
  const playerName =
    typeof payload.player_name === "string"
      ? payload.player_name
      : typeof payload.player_display_name === "string"
        ? payload.player_display_name
        : null;
  if (!playerName) {
    console.log("[DEBUG] handleHumanPlayerAssigned: no player name found");
    return;
  }
  const player = getOrCreatePlayer(playerName);
  player.isHuman = true;
  player.name = playerName;
  gameState.humanPlayerName = playerName;  // Track the human player name
  console.log("[DEBUG] handleHumanPlayerAssigned: Set humanPlayerName to:", gameState.humanPlayerName);
  console.log("[DEBUG] handleHumanPlayerAssigned: player.name =", player.name, "player.isHuman =", player.isHuman);
  
  // Track the player's color from their name or from the event payload
  let playerColor: string | null = null;
  
  // First, try to get color directly from the event payload (backend sends it)
  if (typeof payload.player_color === "string") {
    playerColor = payload.player_color;
    console.log("[DEBUG] handleHumanPlayerAssigned: Got color from payload:", playerColor);
  } else {
    // Fallback: extract from player name
    const colorDetails = resolveColorDetails(playerName);
    console.log("[DEBUG] handleHumanPlayerAssigned: playerName=", playerName, "colorDetails=", colorDetails);
    
    playerColor = colorDetails.baseName ?? null;
    if (!playerColor && playerName) {
      const splitName = playerName.split("-")[0]?.trim();
      if (splitName && ["Red", "Blue", "Purple", "Green", "Orange", "Teal", "Pink", "Yellow"].includes(splitName)) {
        playerColor = splitName;
      }
    }
  }
  
  gameState.humanPlayerColor = playerColor;
  console.log("[DEBUG] handleHumanPlayerAssigned: humanPlayerColor set to", gameState.humanPlayerColor);

  const assignedRole = parseRole(payload.role);
  console.log("[DEBUG] handleHumanPlayerAssigned: role=", payload.role, "parsed=", assignedRole);
  if (assignedRole) {
    gameState.playerRole = assignedRole;
    player.role = assignedRole;
    player.actualRole = assignedRole;
    addMessage(`You have been assigned the role: ${assignedRole}`, "system");
    console.log("[DEBUG] handleHumanPlayerAssigned: role set to", assignedRole);
    // Update role info panel immediately
    updateRoleInfoPanel(assignedRole);
  }
  ensurePlayerCharacter(player);
  updateGameStatus();
  updateRoster();
}

function handleRoleBriefing(
  payload: Record<string, unknown> | undefined,
  eventTimestamp?: number
): void {
  if (!payload) {
    return;
  }

  const briefing =
    typeof payload.briefing === "string"
      ? payload.briefing
      : typeof payload.message === "string"
        ? payload.message
        : null;
  const playerName =
    typeof payload.player_name === "string"
      ? payload.player_name
      : typeof payload.player_display_name === "string"
        ? payload.player_display_name
        : null;

  if (!briefing || !playerName) {
    return;
  }

  if (gameState.humanPlayerName && playerName !== gameState.humanPlayerName) {
    return;
  }

  if (!gameState.humanPlayerName) {
    gameState.humanPlayerName = playerName;
  }

  if (typeof payload.player_color === "string" && !gameState.humanPlayerColor) {
    gameState.humanPlayerColor = payload.player_color;
  }

  const parsedRole = parseRole(payload.role);
  if (parsedRole && gameState.playerRole !== parsedRole) {
    gameState.playerRole = parsedRole;
    updateRoleInfoPanel(parsedRole);
  }

  const timestamp =
    typeof eventTimestamp === "number"
      ? new Date(eventTimestamp * 1000).toISOString()
      : new Date().toISOString();

  const colorSource =
    gameState.humanPlayerColor ||
    (typeof payload.player_color === "string" ? payload.player_color : playerName);
  const colorDetails = resolveColorDetails(colorSource);
  const colorHex = colorDetails?.hex ?? "#2ECC71";

  // Extract just the role assignment line from briefing (hide full LLM prompt with THINKING tags, action formats, etc.)
  // The briefing contains detailed instructions for LLMs that aren't meant for human players
  const lines = briefing.split("\n");
  const summaryLine = lines.find(line => line.includes("You are") && !line.includes("IMPORTANT")) || 
                     lines.find(line => line.trim().length > 0) || 
                     "Role assigned.";
  const displayedBriefing = summaryLine.trim();

  addMessage(displayedBriefing, "system", {
    playerName: "Role Briefing",
    color: colorHex,
    timestamp,
  });

  updateGameStatus();
}

function handlePhaseChanged(payload: Record<string, unknown> | undefined): void {
  if (!payload) {
    return;
  }

  const phaseString =
    typeof payload.phase === "string"
      ? payload.phase
      : typeof payload.new_phase === "string"
        ? payload.new_phase
        : undefined;
  const phase = normalizePhase(phaseString);
  if (phase) {
    gameState.phase = phase;
  }

  const roundNumber =
    typeof payload.round_number === "number"
      ? payload.round_number
      : typeof payload.round === "number"
        ? payload.round
        : undefined;
  if (typeof roundNumber === "number" && !Number.isNaN(roundNumber)) {
    gameState.day = Math.max(1, roundNumber);
  }

  const phaseLabel = formatPhase(gameState.phase);
  addMessage(`Phase changed: ${phaseLabel} (Round ${gameState.day})`, "system");
  updateGameStatus();
  
  // Sync role when phase changes - ensures role is populated
  void syncPlayerRole();
  void fetchGameState();
}

function handlePlayerResponse(payload: Record<string, unknown> | undefined): void {
  if (!payload) {
    console.log("[DEBUG] handlePlayerResponse: payload is undefined");
    return;
  }

  const response = typeof payload.response === "string" ? payload.response : null;
  if (!response) {
    console.log("[DEBUG] handlePlayerResponse: response is not a string", payload);
    return;
  }

  const playerName =
    typeof payload.player_name === "string"
      ? payload.player_name
      : typeof payload.player_display_name === "string"
        ? payload.player_display_name
        : undefined;

  const player = playerName ? getOrCreatePlayer(playerName) : undefined;
  if (player?.isHuman) {
    console.log("[DEBUG] handlePlayerResponse: player is human, skipping");
    return;
  }

  const phase = typeof payload.phase === "string" ? payload.phase : undefined;
  if (!shouldDisplayForCurrentRole(payload.role, phase)) {
    console.log("[DEBUG] handlePlayerResponse: filtered by role", payload.role, "current role:", gameState.playerRole);
    return;
  }

  const resolvedPlayerName = playerName ?? "Unknown Player";

  console.log("[DEBUG] handlePlayerResponse: Adding message -", resolvedPlayerName, ":", response);
  addMessage(response, "llm", {
    playerName: resolvedPlayerName,
    color: player?.color,
    isAlive: player?.isAlive,
    isHuman: player?.isHuman,
  });
}

function handlePlayerAction(payload: Record<string, unknown> | undefined): void {
  if (!payload) {
    return;
  }

  const action = typeof payload.action === "string" ? payload.action : null;
  if (!action) {
    return;
  }

  const phase = typeof payload.phase === "string" ? payload.phase : undefined;
  if (!shouldDisplayForCurrentRole(payload.role, phase)) {
    return;
  }

  const playerName =
    typeof payload.player_name === "string"
      ? payload.player_name
      : typeof payload.player_display_name === "string"
        ? payload.player_display_name
        : "Unknown Player";

  addMessage(`${playerName} performed: ${action}`, "system");

  if (/eliminate|killed|removed/i.test(action)) {
    markPlayerEliminated(playerName);
  }
}

function handleActionTimeout(payload: Record<string, unknown> | undefined): void {
  if (!payload) {
    return;
  }
  const playerName =
    typeof (payload.player as PlayerSnapshot)?.player_name === "string"
      ? (payload.player as PlayerSnapshot).player_name
      : typeof payload.player_name === "string"
        ? payload.player_name
        : "The player";
  addMessage(`${playerName} ran out of time to respond.`, "system");
}

function handleActionFallback(payload: Record<string, unknown> | undefined): void {
  if (!payload) {
    return;
  }
  const playerName =
    typeof (payload.player as PlayerSnapshot)?.player_name === "string"
      ? (payload.player as PlayerSnapshot).player_name
      : typeof payload.player_name === "string"
        ? payload.player_name
        : "The player";
  addMessage(`${playerName} used a fallback response.`, "system");
}

function handleGameEvent(payload: Record<string, unknown> | undefined): void {
  if (!payload) {
    return;
  }

  const text = extractEventText(payload);
  if (text) {
    addMessage(text, "system");
  }

  const eventType = typeof payload.event_type === "string" ? payload.event_type : "";
  if (eventType.toLowerCase().includes("elimination")) {
    const eliminated =
      typeof payload.player_name === "string"
        ? payload.player_name
        : typeof payload.target === "string"
          ? payload.target
          : null;
    if (eliminated) {
      markPlayerEliminated(eliminated);
    }
  }
}

function handlePlayerDied(payload: Record<string, unknown> | undefined): void {
  if (!payload) {
    return;
  }

  const playerName =
    typeof payload.player_name === "string"
      ? payload.player_name
      : typeof payload.player === "string"
        ? payload.player
        : null;

  if (!playerName) {
    return;
  }

  // Extract role from the event payload to ensure we have it
  const roleFromEvent = typeof payload.role === "string" ? payload.role : null;
  markPlayerEliminated(playerName, roleFromEvent);
}

function handleGameWarning(payload: Record<string, unknown> | undefined): void {
  const message = (payload && extractEventText(payload)) ?? "Warning from game server.";
  // Filter out internal debug messages (marked with [internal] prefix)
  if (!message.startsWith("[internal]")) {
    addMessage(message, "system");
  } else {
    console.log("[DEBUG] Filtered internal message:", message);
  }
}

function handleGameError(payload: Record<string, unknown> | undefined): void {
  const message = (payload && extractEventText(payload)) ?? "An error occurred in the game.";
  // Filter out internal debug messages
  if (!message.startsWith("[internal]")) {
    addMessage(message, "system");
  } else {
    console.log("[DEBUG] Filtered internal error:", message);
  }
}

function handleGameEnded(payload: Record<string, unknown> | undefined): void {
  if (gameState.winner) {
    return;
  }

  const winner =
    typeof payload?.winner === "string"
      ? payload.winner
      : typeof payload?.result === "string"
        ? payload.result
        : "Unknown";
  const summary =
    typeof payload?.summary === "string"
      ? payload.summary
      : typeof payload?.message === "string"
        ? payload.message
        : null;

  gameState.winner = winner;
  chatController.setInputEnabled(false);
  updateGameStatus();

  addMessage(`Game over! Winner: ${winner}`, "system");
  if (summary) {
    addMessage(summary, "system");
  }
  stopAutosave();
  if (gameState.gameId) {
    markGameCompleted(gameState.gameId, winner);
  }
}

async function submitPendingAction(message: string): Promise<void> {
  const pending = gameState.pendingAction;
  if (!pending) {
    throw new Error("No pending action to submit.");
  }

  const responsePayload = formatResponseForAction(message, pending);

  try {
    const submission = await authenticatedFetch(`${API_BASE_URL}/api/game/action`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action_id: pending.actionId,
        response: responsePayload,
      }),
    });

    if (!submission.ok) {
      const errorText = await safeReadText(submission);
      
      // Parse JSON error if possible and provide better message
      let errorMessage = errorText || "Server rejected the action.";
      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.detail === "Action not pending.") {
          errorMessage = "Your previous action was already submitted. Please wait for the next prompt.";
        } else if (errorJson.detail) {
          errorMessage = errorJson.detail;
        }
      } catch {
        // Not JSON, use raw text
      }
      
      throw new Error(errorMessage);
    }

    gameState.pendingAction = null;
    chatController.setPrompt("Waiting for the next action...");
    addMessage("Action submitted. Waiting for the other players...", "system");
    updateGameStatus();
  } catch (error) {
    throw error instanceof Error ? error : new Error("Failed to submit the action.");
  }
}

function applyMetadata(metadata: PendingActionMetadata | undefined): void {
  if (!metadata) {
    return;
  }

  const phase = normalizePhase(
    typeof metadata.phase === "string"
      ? metadata.phase
      : typeof metadata.phase_type === "string"
        ? metadata.phase_type
        : undefined
  );
  if (phase) {
    gameState.phase = phase;
  }

  const roundNumber =
    typeof metadata.round_number === "number"
      ? metadata.round_number
      : typeof metadata.round === "number"
        ? metadata.round
        : undefined;
  if (typeof roundNumber === "number" && !Number.isNaN(roundNumber)) {
    gameState.day = Math.max(1, roundNumber);
  }

  updateGameStatus();
}

function updatePlayerFromSnapshot(snapshot: PlayerSnapshot | undefined): void {
  if (!snapshot) {
    console.log("[DEBUG] updatePlayerFromSnapshot: snapshot is undefined");
    return;
  }
  const playerName = snapshot.player_name ?? snapshot.model_name;
  if (!playerName) {
    console.log("[DEBUG] updatePlayerFromSnapshot: no player name");
    return;
  }

  console.log("[DEBUG] updatePlayerFromSnapshot:", {
    playerName,
    isHuman: snapshot.is_human,
    role: snapshot.role,
    alive: snapshot.alive,
  });

  const player = getOrCreatePlayer(playerName);
  player.isAlive = snapshot.alive ?? player.isAlive;
  if (snapshot.is_human) {
    player.isHuman = true;
  }

  const colorDetails = resolveColorDetails(player.name);
  if (colorDetails.hex) {
    player.color = colorDetails.hex;
  }
  if (colorDetails.index !== undefined) {
    player.colorIndex = colorDetails.index;
  }
  
  // Set humanPlayerColor if this is the human player
  if (snapshot.is_human && colorDetails.baseName && !gameState.humanPlayerColor) {
    gameState.humanPlayerColor = colorDetails.baseName;
    console.log("[DEBUG] updatePlayerFromSnapshot: set humanPlayerColor to", gameState.humanPlayerColor);
  }

  const role = parseRole(snapshot.role);
  console.log("[DEBUG] updatePlayerFromSnapshot: parsed role=", role, "player.isHuman=", player.isHuman);
  if (role && player.isHuman) {
    console.log("[DEBUG] updatePlayerFromSnapshot: setting role for human player");
    player.role = role;
    player.actualRole = role;
    gameState.playerRole = role;
    // Update role info panel when role is assigned from snapshot
    updateRoleInfoPanel(role);
  } else if (role) {
    player.actualRole = role;
  }

  ensurePlayerCharacter(player);
  updateRoster();
}

function getOrCreatePlayer(name: string): Player {
  const key = name;
  let player = playersByName.get(key);
  if (player) {
    player.name = name;
    ensurePlayerCharacter(player);
    return player;
  }

  const colorDetails = resolveColorDetails(name);

  player = {
    id: key,
    name,
    role: null,
    actualRole: null,
    isAlive: true,
    isHuman: false,
    color: colorDetails.hex,
    colorIndex: colorDetails.index,
  };
  playersByName.set(key, player);
  if (!gameState.players.includes(player)) {
    gameState.players.push(player);
  }
  ensurePlayerCharacter(player);
  updateRoster();
  return player;
}

function findPlayerByName(name: string): Player | undefined {
  const direct = playersByName.get(name);
  if (direct) {
    return direct;
  }
  const lower = name.toLowerCase();
  return gameState.players.find((player) => player.name.toLowerCase() === lower);
}

function ensurePlayerCharacter(player: Player): void {
  const colorDetails = resolveColorDetails(player.name);
  if (colorDetails.hex && !player.color) {
    player.color = colorDetails.hex;
  }
  if (colorDetails.index !== undefined && player.colorIndex === undefined) {
    player.colorIndex = colorDetails.index;
  }

  // Skip 3D character creation on mobile
  if (IS_MOBILE) {
    return;
  }

  const totalSlots = PLAYER_COLOR_NAMES.length;
  const slotIndex = player.colorIndex ?? sceneCharacters.length;
  const baseColor = player.color ?? colorDetails.hex ?? DEFAULT_PLAYER_COLOR;
  const activeColor = player.isAlive ? baseColor : DEAD_PLAYER_COLOR;

  if (!player.character && scene) {
    const character = createCharacter(slotIndex, scene, {
      color: activeColor,
      isHuman: player.isHuman,
      label: player.name,
      totalSlots,
    });
    sceneCharacters.push(character);
    player.character = character;
  }

  updateCharacterAppearance(player);
}

function updateCharacterAppearance(player: Player): void {
  if (!player.character) {
    return;
  }
  const colorDetails = resolveColorDetails(player.name);
  if (!player.color && colorDetails.hex) {
    player.color = colorDetails.hex;
  }
  const baseColor = player.color ?? colorDetails.hex ?? DEFAULT_PLAYER_COLOR;
  const activeColor = player.isAlive ? baseColor : DEAD_PLAYER_COLOR;
  player.character.setColor(activeColor);
  player.character.setAlive(player.isAlive);
  player.character.setHuman(player.isHuman);
}

async function spawnAllPlayers(): Promise<void> {
  // Spawn all players that we know about from gameState.players
  // This includes players from handlePlayerSetup and handleHumanPlayerAssigned
  console.log("[DEBUG] spawnAllPlayers: Spawning", gameState.players.length, "players");
  
  for (const player of gameState.players) {
    // Ensure character is created and visible
    ensurePlayerCharacter(player);
    updateCharacterAppearance(player);
  }

  console.log("[DEBUG] spawnAllPlayers: Spawned all players, sceneCharacters count:", sceneCharacters.length);
}

function markPlayerEliminated(name: string, roleFromEvent?: string | null): void {
  const player = findPlayerByName(name);
  if (player) {
    player.isAlive = false;
    updateCharacterAppearance(player);
    updateRoster();
    
    // Show role reveal based on flip mode setting
    // Prefer role from the event (most reliable), then check player.actualRole
    const role = roleFromEvent || player.actualRole;
    if (currentGameSettings.flipMode !== "none" && role) {
      const roleName = (typeof role === "string" ? role : role).toUpperCase();
      
      if (currentGameSettings.flipMode === "alignment") {
        // Show only Mafia or Town alignment
        const alignment = roleName === "MAFIA" ? "MAFIA" : "TOWN";
        addMessage(`${player.name} was eliminated. Alignment: ${alignment}`, "system");
      } else if (currentGameSettings.flipMode === "full") {
        // Show full role name
        addMessage(`${player.name} was eliminated. Role: ${roleName}`, "system");
      }
    }
    
    if (player.isHuman) {
      gameState.pendingAction = null;
      chatController.setPrompt(`${player.name} has been eliminated.`);
      chatController.setInputEnabled(false);
      chatController.updateSkipVoteButton(false, false);
    }
  }
}

function updateGameStatus(): void {
  if (statusRoundElement) {
    statusRoundElement.textContent = `Round ${Math.max(1, gameState.day)}`;
  }

  if (statusPhaseElement) {
    statusPhaseElement.textContent = gameState.winner
      ? "Game Over"
      : formatPhase(gameState.phase);
  }

  if (statusRoleElement) {
    statusRoleElement.textContent = formatRoleLabel(gameState.playerRole);
  }

  if (connectionStatusElement) {
    connectionStatusElement.classList.toggle("connected", gameState.connected);
  }

  if (connectionStatusLabel) {
    connectionStatusLabel.textContent = gameState.connected ? "Connected" : "Connecting...";
    // Hide text on mobile, keep only the indicator dot
    if (IS_MOBILE) {
      connectionStatusLabel.textContent = "";
    }
  }

  const legacyStatusElement = document.getElementById("game-status");
  if (legacyStatusElement) {
    if (gameState.winner) {
      legacyStatusElement.textContent = `Game Over – ${gameState.winner}`;
      return;
    }
    const roleLabel = gameState.playerRole ? ` | Role: ${gameState.playerRole}` : "";
    const connectionSuffix = gameState.connected ? "" : " (connecting...)";
    legacyStatusElement.textContent = `Round ${gameState.day} – ${formatPhase(
      gameState.phase
    )}${roleLabel}${connectionSuffix}`;
  }

  if (debugController.enabled) {
    updateDebugPanelState();
  }

  updateActiveGameSummary();
}

function updateActiveGameSummary(): void {
  if (!gameState.gameId) {
    return;
  }

  const existingSummary = gameSummaries.get(gameState.gameId);
  if (!existingSummary) {
    return;
  }

  const status: GameLifecycleStatus = gameState.winner ? "completed" : "active";
  gameSummaries.set(gameState.gameId, {
    ...existingSummary,
    round: Math.max(1, gameState.day),
    phase: gameState.phase,
    winner: gameState.winner,
    status,
    updatedAt: Date.now(),
  });
  updateGameSwitcher();
}

function updateDebugPanelState(): void {
  if (!debugController.enabled) {
    return;
  }
  debugController.updateState(gameState);
  debugController.setPaused(gameState.isPaused);
  syncDebugControls();
}

function syncDebugControls(): void {
  if (!debugController.enabled) {
    return;
  }
  const hasGame = Boolean(gameState.gameId);
  debugController.setControlsEnabled(hasGame && !debugToggleInFlight);
}

function handleGameIdUpdate(): void {
  if (gameState.gameId) {
    startAutosave();
  } else {
    stopAutosave();
  }

  if (!debugController.enabled) {
    return;
  }
  if (gameState.gameId) {
    syncDebugControls();
    startDebugPolling();
  } else {
    stopDebugPolling();
    gameState.isPaused = false;
    debugController.setPaused(false);
    debugController.setRecentEvents([]);
    applyPauseSideEffects();
    syncDebugControls();
  }
}

function startDebugPolling(): void {
  if (!debugController.enabled || debugPollTimer || !gameState.gameId) {
    return;
  }
  void refreshDebugSnapshot();
  debugPollTimer = window.setInterval(() => {
    void refreshDebugSnapshot();
  }, 2000);
}

function stopDebugPolling(): void {
  if (debugPollTimer) {
    clearInterval(debugPollTimer);
    debugPollTimer = undefined;
  }
}

function startAutosave(): void {
  if (autosaveTimer || !gameState.gameId) {
    return;
  }
  autosaveTimer = window.setInterval(() => {
    void executeAutosave();
  }, AUTOSAVE_INTERVAL_MS);
  void executeAutosave();
}

function stopAutosave(): void {
  if (autosaveTimer) {
    clearInterval(autosaveTimer);
    autosaveTimer = undefined;
  }
}

async function executeAutosave(): Promise<void> {
  if (!gameState.gameId) {
    return;
  }
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/game/${gameState.gameId}/autosave`,
      {
        method: "POST",
      }
    );
    if (!response.ok) {
      const message = await safeReadText(response);
      throw new Error(message ?? "Autosave failed");
    }
  } catch (error) {
    console.debug("[autosave] Failed:", error);
  }
}

async function handleGameOver(gameOverData: Record<string, unknown>): Promise<void> {
  const gameOverInfo: GameOverData = {
    winner: (gameOverData.winner as string) || "Unknown",
    reason: (gameOverData.reason as string) || "Game concluded",
    rounds_played: (gameOverData.rounds_played as number) || 0,
    mafia_survivors: (gameOverData.mafia_survivors as string[]) || [],
    mafia_eliminated: (gameOverData.mafia_eliminated as string[]) || [],
    villagers_remaining: (gameOverData.villagers_remaining as string[]) || [],
    villagers_eliminated: (gameOverData.villagers_eliminated as string[]) || [],
  };

  endscreen.show(gameOverInfo, () => {
    if (gameState.gameId) {
      markGameCompleted(gameState.gameId, gameOverInfo.winner);
    }
    endscreen.hide();
    void handleStartGameRequest();
  });
}

async function refreshDebugSnapshot(): Promise<void> {
  if (!debugController.enabled || !gameState.gameId) {
    return;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/game/${gameState.gameId}/debug`);
    if (!response.ok) {
      return;
    }
    const snapshot = (await response.json()) as DebugSnapshot & { game_over?: Record<string, unknown> };
    const wasPaused = gameState.isPaused;
    gameState.isPaused = snapshot.paused;
    if (gameState.isPaused !== wasPaused) {
      applyPauseSideEffects();
    }
    
    // Check for game over
    if (snapshot.game_over && !endscreen.isVisible()) {
      void handleGameOver(snapshot.game_over);
    }
    
    const recentEvents = (snapshot.recent_events ?? [])
      .map((entry) => convertEventToText(entry))
      .filter((text): text is string => Boolean(text));
    debugController.setRecentEvents(recentEvents);
    updateDebugPanelState();
  } catch (error) {
    console.debug("[debug] Failed to refresh debug snapshot:", error);
  }
}

async function handleDebugToggle(shouldPause: boolean): Promise<void> {
  if (!debugController.enabled || !gameState.gameId) {
    return;
  }

  debugToggleInFlight = true;
  syncDebugControls();
  try {
    const endpoint = shouldPause ? "pause" : "resume";
    const response = await fetch(`${API_BASE_URL}/api/game/${gameState.gameId}/${endpoint}`, {
      method: "POST",
    });
    if (!response.ok) {
      const errorText = await safeReadText(response);
      throw new Error(errorText || `Failed to ${endpoint} game`);
    }
    const wasPaused = gameState.isPaused;
    gameState.isPaused = shouldPause;
    applyPauseSideEffects();
    debugController.setPaused(shouldPause);
    if (wasPaused !== shouldPause) {
      addMessage(
        shouldPause ? "Game paused (debug mode)." : "Game resumed (debug mode).",
        "system"
      );
    }
    if (!shouldPause) {
      await refreshPendingAction();
    }
    await refreshDebugSnapshot();
  } catch (error) {
    console.error(
      `[debug] Unable to ${shouldPause ? "pause" : "resume"} game:`,
      error instanceof Error ? error.message : error
    );
  } finally {
    debugToggleInFlight = false;
    syncDebugControls();
  }
}

function applyPauseSideEffects(): void {
  if (gameState.isPaused) {
    chatController.setInputEnabled(false);
    chatController.updateSkipVoteButton(false, false);
    return;
  }

  if (gameState.pendingAction) {
    const metadata = gameState.pendingAction.metadata ?? {};
    const isVotingPhase = metadata.action === "day_voting";
    const canInteract = isLocalPlayerAlive();
    chatController.updateSkipVoteButton(isVotingPhase, canInteract);
    chatController.setInputEnabled(canInteract);
  } else {
    chatController.setInputEnabled(false);
    chatController.updateSkipVoteButton(false, false);
  }
}

function convertEventToText(event: Record<string, unknown>): string | undefined {
  const type = typeof event.type === "string" ? event.type : "event";
  const payload = (event.payload ?? {}) as Record<string, unknown>;

  const messageCandidates: Array<unknown> = [
    payload.message,
    payload.summary,
    payload.details,
    payload.content,
    payload.action,
  ];
  for (const candidate of messageCandidates) {
    if (typeof candidate === "string" && candidate.trim().length > 0) {
      return `[${type}] ${candidate.trim()}`;
    }
  }

  const playerName = typeof payload.player_name === "string" ? payload.player_name : undefined;
  if (playerName) {
    return `[${type}] ${playerName}`;
  }

  return `[${type}]`;
}

async function restoreChatHistory(history: ChatHistoryEntry[]): Promise<void> {
  if (historyRestored) {
    return;
  }
  if (!Array.isArray(history) || history.length === 0) {
    historyRestored = true;
    return;
  }

  const existingMessageKeys = new Set(
    gameState.messages.map((message) =>
      createMessageKey(message.text, message.sender, message.playerName)
    )
  );

  for (const entry of history) {
    const normalized = normalizeHistoryEntry(entry);
    if (!normalized) {
      continue;
    }

    const { text, type, playerName, isHuman, timestamp } = normalized;

    const messageKeyValue = createMessageKey(text, type, playerName);
    if (existingMessageKeys.has(messageKeyValue)) {
      continue;
    }

    const player = playerName ? findPlayerByName(playerName) : undefined;

    chatController.addMessage(text, type, {
      playerName,
      color: player?.color,
      isAlive: player?.isAlive,
      isHuman: player?.isHuman ?? isHuman,
      timestamp,
    });

    // Add small delay between messages for readability (50ms)
    await new Promise((resolve) => setTimeout(resolve, 50));
  }

  historyRestored = true;
}

function updateRoster(): void {
  if (!rosterListElement) {
    return;
  }

  rosterListElement.innerHTML = "";

  if (gameState.players.length === 0) {
    const placeholder = document.createElement("li");
    placeholder.classList.add("player-roster-item", "empty");
    placeholder.textContent = "Waiting for players...";
    rosterListElement.appendChild(placeholder);
    return;
  }

  const sortedPlayers = [...gameState.players].sort((playerA, playerB) => {
    const indexA = playerA.colorIndex ?? Number.MAX_SAFE_INTEGER;
    const indexB = playerB.colorIndex ?? Number.MAX_SAFE_INTEGER;
    return indexA - indexB;
  });

  for (const player of sortedPlayers) {
    const item = document.createElement("li");
    item.classList.add("player-roster-item", player.isAlive ? "alive" : "dead");
    item.dataset.playerId = player.id;

    const badge = document.createElement("span");
    badge.classList.add("player-badge");

    const badgeColor = player.isAlive ? player.color : DEAD_PLAYER_COLOR;
    if (badgeColor) {
      badge.style.backgroundColor = badgeColor;
      badge.style.color = getContrastingTextColor(badgeColor);
    }

    const badgeLabel = player.colorIndex !== undefined ? player.colorIndex + 1 : "?";
    badge.textContent = typeof badgeLabel === "number" ? `P${badgeLabel}` : "P";

    const nameSpan = document.createElement("span");
    nameSpan.classList.add("player-name");
    nameSpan.textContent = player.name;

    const statusSpan = document.createElement("span");
    statusSpan.classList.add("player-status");
    statusSpan.textContent = player.isAlive ? "Alive" : "Eliminated";

    if (player.isAlive && player.color) {
      item.style.borderColor = hexToRgba(player.color, 0.45);
      item.style.backgroundColor = hexToRgba(player.color, 0.18);
    }

    item.appendChild(badge);
    item.appendChild(nameSpan);
    item.appendChild(statusSpan);

    rosterListElement.appendChild(item);
  }

  if (debugController.enabled) {
    updateDebugPanelState();
  }
  
  // Update mobile player status bar
  updateMobilePlayerStatus();
}

function updateMobilePlayerStatus(): void {
  if (!IS_MOBILE || !mobilePlayerStatus) {
    return;
  }
  
  // Show the mobile status bar
  mobilePlayerStatus.style.display = "flex";
  mobilePlayerStatus.innerHTML = "";
  
  if (gameState.players.length === 0) {
    return;
  }
  
  const sortedPlayers = [...gameState.players].sort((playerA, playerB) => {
    const indexA = playerA.colorIndex ?? Number.MAX_SAFE_INTEGER;
    const indexB = playerB.colorIndex ?? Number.MAX_SAFE_INTEGER;
    return indexA - indexB;
  });
  
  for (const player of sortedPlayers) {
    const indicator = document.createElement("div");
    indicator.classList.add("mobile-player-indicator", player.isAlive ? "alive" : "dead");
    
    const badgeColor = player.isAlive ? player.color : DEAD_PLAYER_COLOR;
    if (badgeColor) {
      indicator.style.borderColor = badgeColor;
      indicator.style.backgroundColor = hexToRgba(badgeColor, 0.15);
    }
    
    // Color dot
    const dot = document.createElement("div");
    dot.classList.add("mobile-player-dot");
    if (badgeColor) {
      dot.style.backgroundColor = badgeColor;
    }
    
    // Player name (short version)
    const name = document.createElement("span");
    const shortName = player.name.split("-")[0] || player.name;
    name.textContent = shortName;
    name.style.color = player.isAlive ? "#fff" : "#999";
    
    indicator.appendChild(dot);
    indicator.appendChild(name);
    mobilePlayerStatus.appendChild(indicator);
  }
}

function isLocalPlayerAlive(): boolean {
  const humanPlayer = gameState.players.find((player) => player.isHuman);
  if (!humanPlayer) {
    return true;
  }
  return humanPlayer.isAlive;
}

function resetPlayerRegistry(): void {
  sceneCharacters.forEach((character) => {
    scene.remove(character.model);
  });
  sceneCharacters.length = 0;

  gameState.players.length = 0;
  playersByName.clear();
  updateRoster();
}

function resetChat(): void {
  gameState.messages.length = 0;
  const chatContainer = document.getElementById("chat-container") as HTMLDivElement | null;
  if (chatContainer) {
    chatContainer.innerHTML = "";
  }
  chatController.setPrompt(null);
  chatController.setInputEnabled(false);
  chatController.updateSkipVoteButton(false, false);
}

interface NormalizedHistoryEntryResult {
  text: string;
  type: ChatMessageType;
  playerName?: string;
  isHuman: boolean;
  timestamp?: string;
}

function normalizeHistoryEntry(entry: ChatHistoryEntry): NormalizedHistoryEntryResult | null {
  if (!entry) {
    return null;
  }

  const rawContent =
    typeof entry.content === "string" ? entry.content.trim() : "";
  if (!rawContent) {
    return null;
  }

  const baseName =
    (typeof entry.player_name === "string" && entry.player_name.trim().length > 0
      ? entry.player_name.trim()
      : null) ??
    (typeof entry.speaker === "string" && entry.speaker.trim().length > 0
      ? entry.speaker.trim()
      : null) ??
    undefined;

  const normalizedType = typeof entry.type === "string" ? entry.type.toLowerCase() : undefined;

  if (normalizedType === "system" || normalizedType === "event" || normalizedType === "status") {
    return {
      text: rawContent,
      type: "system",
      playerName: baseName,
      isHuman: false,
    };
  }

  if (normalizedType === "last_words") {
    const name = baseName ?? "Unknown Player";
    return {
      text: `${name}'s last words: ${rawContent}`,
      type: "system",
      playerName: baseName,
      isHuman: false,
    };
  }

  const isHuman = entry.is_human ?? false;
  const type: ChatMessageType = isHuman ? "player" : "llm";

  return {
    text: rawContent,
    type,
    playerName: baseName,
    isHuman,
  };
}

function createMessageKey(
  text: string,
  sender: ChatMessageType,
  playerName?: string | null
): string {
  return `${sender}|${(playerName ?? "").toLowerCase()}|${text}`;
}

function hexToRgba(hexColor: string | undefined, alpha: number): string {
  if (!hexColor) {
    return `rgba(255, 255, 255, ${alpha})`;
  }
  const sanitized = hexColor.replace("#", "");
  const bigint = parseInt(sanitized, 16);
  const hasShortFormat = sanitized.length === 3;

  if (Number.isNaN(bigint)) {
    return `rgba(255, 255, 255, ${alpha})`;
  }

  const r = hasShortFormat ? ((bigint >> 8) & 0xf) * 17 : (bigint >> 16) & 0xff;
  const g = hasShortFormat ? ((bigint >> 4) & 0xf) * 17 : (bigint >> 8) & 0xff;
  const b = hasShortFormat ? (bigint & 0xf) * 17 : bigint & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function getContrastingTextColor(hexColor: string): string {
  const sanitized = hexColor.replace("#", "");
  const bigint = parseInt(sanitized, 16);
  const hasShortFormat = sanitized.length === 3;

  if (Number.isNaN(bigint)) {
    return "#111111";
  }

  const r = hasShortFormat ? ((bigint >> 8) & 0xf) * 17 : (bigint >> 16) & 0xff;
  const g = hasShortFormat ? ((bigint >> 4) & 0xf) * 17 : (bigint >> 8) & 0xff;
  const b = hasShortFormat ? (bigint & 0xf) * 17 : bigint & 0xff;

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.65 ? "#111111" : "#ffffff";
}

function addMessage(text: string, type: ChatMessageType, options?: ChatMessageOptions): void {
  chatController.addMessage(text, type, options);
}

function buildWsUrl(gameId: string): string {
  const url = new URL(API_BASE_URL);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = `/ws/game/${gameId}`;
  url.search = "";
  url.hash = "";
  return url.toString();
}

function normalizePhase(phase: string | undefined): GamePhase | null {
  if (!phase) {
    return null;
  }

  const upper = phase.toUpperCase();
  if (upper.includes("GREETING")) {
    return GamePhase.GREETING;
  }
  if (upper.includes("NIGHT_0")) {
    return GamePhase.NIGHT_0;
  }
  if (upper.includes("NIGHT")) {
    return GamePhase.NIGHT;
  }
  if (upper.includes("VOTING")) {
    return GamePhase.VOTING;
  }
  if (upper.includes("DAY")) {
    return GamePhase.DAY;
  }
  if (upper.includes("SETUP")) {
    return GamePhase.SETUP;
  }
  if (upper.includes("GAME_OVER") || upper.includes("ENDED")) {
    return GamePhase.GAME_OVER;
  }
  return null;
}

function formatRoleLabel(role: Role | null): string {
  if (!role) {
    return "Unknown";
  }
  const lower = role.toLowerCase();
  const roleText = lower.charAt(0).toUpperCase() + lower.slice(1);
  
  // Add color text if available
  console.log("[DEBUG] formatRoleLabel: color=", gameState.humanPlayerColor, "role=", roleText);
  if (gameState.humanPlayerColor) {
    return `${gameState.humanPlayerColor} ${roleText}`;
  }
  
  return roleText;
}

function formatPhase(phase: GamePhase): string {
  switch (phase) {
    case GamePhase.GREETING:
      return "Greeting Round";
    case GamePhase.NIGHT_0:
      return "Night 0 - Role Briefing";
    case GamePhase.NIGHT:
      return "Night Phase";
    case GamePhase.DAY:
      return "Day Phase";
    case GamePhase.VOTING:
      return "Voting Phase";
    case GamePhase.GAME_OVER:
      return "Game Over";
    case GamePhase.SETUP:
    default:
      return "Setup";
  }
}

function parseRole(role: unknown): Role | null {
  if (typeof role !== "string") {
    return null;
  }
  const upper = role.toUpperCase();
  if (upper.includes("MAFIA")) {
    return Role.MAFIA;
  }
  if (upper.includes("SHERIFF") || upper.includes("DETECTIVE")) {
    return Role.SHERIFF;
  }
  if (upper.includes("DOCTOR")) {
    return Role.DOCTOR;
  }
  if (upper.includes("VILLAGER")) {
    return Role.VILLAGER;
  }
  return null;
}

function shouldDisplayForCurrentRole(role: unknown, phase?: unknown): boolean {
  if (typeof role !== "string") {
    return true;
  }
  const upper = role.toUpperCase();
  
  // Use provided phase if available, otherwise use current game phase
  const checkPhase = typeof phase === "string" ? phase.toLowerCase() : gameState.phase;
  const isNightPhase = checkPhase === GamePhase.NIGHT || checkPhase === "night";
  
  // Only filter during NIGHT phase
  // During DAY phase, ALL players' responses should be visible
  if (isNightPhase) {
    // Hide night-only role information from players not in that role
    if (upper.includes("MAFIA") && gameState.playerRole !== Role.MAFIA) {
      return false;
    }
    if (upper.includes("DOCTOR") && gameState.playerRole !== Role.DOCTOR) {
      return false;
    }
    if (
      (upper.includes("SHERIFF") || upper.includes("DETECTIVE")) &&
      gameState.playerRole !== Role.SHERIFF
    ) {
      return false;
    }
  }
  
  // During DAY phase, always display (phase-based filtering should be done server-side)
  return true;
}

function formatResponseForAction(message: string, pending: PendingAction): string {
  if (/\bACTION:/i.test(message)) {
    return message.trim();
  }

  const actionType = typeof pending.metadata.action === "string" ? pending.metadata.action : null;
  if (!actionType) {
    return message.trim();
  }

  const availablePlayersRaw = Array.isArray(pending.metadata.available_players)
    ? pending.metadata.available_players
    : [];
  const availablePlayers = availablePlayersRaw.filter(
    (candidate) => candidate && candidate.alive !== false
  );
  const target = findTargetFromMessage(message, availablePlayers);
  
  // Check if abstaining from vote
  const isAbstaining = /\b(abstain|no vote|skip|skip vote|pass)\b/i.test(message);

  switch (actionType) {
    case "day_discussion":
    case "last_words":
      return message.trim();
    case "day_voting":
      if (isAbstaining) {
        return `${message.trim()}\nVOTE: ABSTAIN`;
      }
      if (!target) {
        throw new Error("Please mention exactly who you want to vote for.");
      }
      return `${message.trim()}\nACTION: Vote ${target}`;
    case "night_mafia":
      if (!target) {
        throw new Error("Please specify a target to eliminate (e.g. Kill Alex).");
      }
      return `${message.trim()}\nACTION: Kill ${target}`;
    case "night_doctor":
      if (!target) {
        throw new Error("Please specify who you want to protect.");
      }
      return `${message.trim()}\nACTION: Protect ${target}`;
    case "night_sheriff":
    case "night_detective":
      if (!target) {
        throw new Error("Please specify who you want to investigate.");
      }
      return `${message.trim()}\nACTION: Investigate ${target}`;
    default:
      return message.trim();
  }
}

function findTargetFromMessage(
  message: string,
  candidates: PlayerSnapshot[]
): string | null {
  const lowerMessage = message.toLowerCase();

  for (const candidate of candidates) {
    const candidateName = candidate.player_name ?? candidate.model_name;
    if (!candidateName || candidate.alive === false) {
      continue;
    }
    if (lowerMessage.includes(candidateName.toLowerCase())) {
      return candidateName;
    }
  }

  const explicitMatch = message.match(
    /(vote|kill|eliminate|target|protect|investigate)\s+([a-zA-Z0-9_\- ]+)/i
  );
  if (explicitMatch) {
    const candidateName = explicitMatch[2].trim().toLowerCase();
    const candidate = candidates.find((player) => {
      const name = player.player_name ?? player.model_name;
      return name ? name.toLowerCase() === candidateName && player.alive !== false : false;
    });
    if (candidate) {
      return candidate.player_name ?? candidate.model_name ?? null;
    }
  }

  return null;
}

function extractEventText(payload: Record<string, unknown>): string | null {
  const possibleFields: Array<keyof GameEventPayload> = [
    "message",
    "details",
    "summary",
    "content",
    "prompt",
  ];

  for (const field of possibleFields) {
    const value = payload[field];
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }
  return null;
}

async function safeReadText(response: Response): Promise<string | null> {
  try {
    const text = await response.text();
    return text || null;
  } catch {
    return null;
  }
}

window.addEventListener("beforeunload", () => {
  stopAutosave();
});

window.addEventListener("load", startGame);
(window as unknown as { gameState: GameState }).gameState = gameState;
